// ============================================================================
// packages/shared/src/capabilities.ts
// ----------------------------------------------------------------------------
// Constantes des capabilities de la plateforme. Doit rester en SYNC avec la
// migration 0015_capabilities.sql (table public.capabilities + matrice
// public.role_default_capabilities). Les keys ici sont la source de vérité
// côté code TS pour éviter les fautes de frappe à l'usage.
//
// Ajout d'une capability :
//   1. Migration SQL qui INSERT dans public.capabilities (+ matrice si défaut)
//   2. Ajout de la constante ici
//   3. Si utilisée pour gate une route Worker : ajouter le check dans le
//      middleware avec requireCapability(C.NEW_CAP)
//   4. Si utilisée pour gate une UI : useCapability(C.NEW_CAP) côté React
// ============================================================================

export const Capability = {
  // ─── Auth / RH ─────────────────────────────────────────────────────────
  ADMIN_ALL:                      'admin.all',
  USERS_READ_ALL:                 'users.read_all',
  USERS_WRITE:                    'users.write',
  USERS_RESET_PASSWORD:           'users.reset_password',
  PERMISSIONS_MANAGE:             'permissions.manage',

  // ─── Locations / structure ─────────────────────────────────────────────
  LOCATIONS_WRITE:                'locations.write',
  USER_LOCATIONS_WRITE:           'user_locations.write',

  // ─── Module planning ───────────────────────────────────────────────────
  POSITIONS_WRITE:                'positions.write',
  PLANNING_READ_ALL:              'planning.read_all',
  PLANNING_READ_OWN_LOCATIONS:    'planning.read_own_locations',
  PLANNING_WRITE_OWN_LOCATIONS:   'planning.write_own_locations',
  PLANNING_PUBLISH:               'planning.publish',
  PLANNING_GENERATE:              'planning.generate',
  RECURRING_ASSIGNMENTS_WRITE:    'recurring_assignments.write',
  ABSENCES_MANAGE:                'absences.manage',
  ABSENCES_REQUEST:               'absences.request',
  PLANNING_SIGN:                  'planning.sign',
  PLANNING_EXTRA_REQUEST:         'planning.extra_request',
  SWAP_APPROVE:                   'swap.approve',

  // ─── Module menu ───────────────────────────────────────────────────────
  MENU_EDIT_CATALOG:              'menu.edit_catalog',
  MENU_VALIDATE:                  'menu.validate',
  MENU_COMPOSE:                   'menu.compose',

  // ─── Module hygiène ────────────────────────────────────────────────────
  HYGIENE_READ:                   'hygiene.read',
  HYGIENE_MANAGE:                 'hygiene.manage',

  // ─── Module légal ──────────────────────────────────────────────────────
  LEGAL_READ:                     'legal.read',
  LEGAL_MANAGE:                   'legal.manage',

  // ─── Module retours produits ───────────────────────────────────────────
  FEEDBACK_READ:                  'feedback.read',
  FEEDBACK_WRITE:                 'feedback.write',

  // ─── Module caisse ─────────────────────────────────────────────────────
  CASH_READ:                      'cash.read',
  CASH_WRITE:                     'cash.write',
  CASH_MANAGE:                    'cash.manage',
  // Validation banque niveau 2 (service gestion) — accès sur désignation.
  CASH_BANK_VALIDATE:             'cash.bank_validate',

  // ─── Module supervision (droits supérieurs — accès sur désignation) ─────
  SUPERVISION_READ:               'supervision.read',

  // ─── Reporting CA (tendances pluriannuelles — accès sur désignation) ─────
  REPORTING_READ:                 'reporting.read',

  // ─── Reporting caisse (archives Clyo NF525 par PV — accès sur désignation) ─
  CAISSE_READ:                    'caisse.read',
  CAISSE_MANAGE:                  'caisse.manage',

  // ─── Module tenues (garde-robe — accès sur désignation) ─────────────────
  WARDROBE_READ:                  'wardrobe.read',
  WARDROBE_MANAGE:                'wardrobe.manage',

  // ─── Module achats (factures fournisseurs — accès sur désignation) ──────
  ACHATS_READ:                    'achats.read',
  ACHATS_MANAGE:                  'achats.manage',

  // ─── Module newsletter (collecte d'emails clients — accès sur désignation) ─
  NEWSLETTER_MANAGE:              'newsletter.manage',

  // ─── Module candidatures / CVthèque (RH — admin + manager) ──────────────
  CANDIDATURES_MANAGE:            'candidatures.manage',

  // ─── Module production cuisine (fiches techniques + prod — accès sur désignation) ─
  CUISINE_READ:                   'cuisine.read',
  CUISINE_PRODUCE:                'cuisine.produce',
  CUISINE_MANAGE:                 'cuisine.manage',

  // ─── Module événementiel (partenaires presta + suivi commercial — accès sur désignation) ─
  EVENT_ACCESS:                   'event.access',
} as const;

export type CapabilityKey = typeof Capability[keyof typeof Capability];

// ─── Rôles ─────────────────────────────────────────────────────────────────
export const Role = {
  ADMIN:   'admin',
  MANAGER: 'manager',
  STAFF:   'staff',
  CHEF:    'chef',
} as const;

export type RoleKey = typeof Role[keyof typeof Role];

/**
 * Matrice rôle → capabilities par défaut (miroir de
 * public.role_default_capabilities). À utiliser uniquement pour des
 * **fallback côté UI** : la source de vérité est la DB, et tout gate
 * sensible côté Worker doit appeler `public.has_capability(...)`.
 *
 * Utilité concrète : afficher la matrice dans Réglages → Permissions sans
 * faire de roundtrip à la DB.
 */
export const ROLE_DEFAULTS: Record<RoleKey, CapabilityKey[]> = {
  admin: [
    Capability.ADMIN_ALL,
    Capability.USERS_READ_ALL,
    Capability.USERS_WRITE,
    Capability.USERS_RESET_PASSWORD,
    Capability.PERMISSIONS_MANAGE,
    Capability.LOCATIONS_WRITE,
    Capability.USER_LOCATIONS_WRITE,
    Capability.POSITIONS_WRITE,
    Capability.PLANNING_READ_ALL,
    Capability.PLANNING_WRITE_OWN_LOCATIONS,
    Capability.PLANNING_PUBLISH,
    Capability.PLANNING_GENERATE,
    Capability.RECURRING_ASSIGNMENTS_WRITE,
    Capability.ABSENCES_MANAGE,
    Capability.SWAP_APPROVE,
    Capability.MENU_EDIT_CATALOG,
    Capability.MENU_VALIDATE,
    Capability.MENU_COMPOSE,
    Capability.HYGIENE_READ,
    Capability.HYGIENE_MANAGE,
    Capability.LEGAL_READ,
    Capability.LEGAL_MANAGE,
    Capability.FEEDBACK_READ,
    Capability.FEEDBACK_WRITE,
    Capability.CASH_READ,
    Capability.CASH_WRITE,
    Capability.CASH_MANAGE,
    Capability.NEWSLETTER_MANAGE,
  ],
  manager: [
    Capability.USERS_READ_ALL,
    Capability.PLANNING_READ_OWN_LOCATIONS,
    Capability.PLANNING_WRITE_OWN_LOCATIONS,
    Capability.PLANNING_PUBLISH,
    Capability.PLANNING_GENERATE,
    Capability.RECURRING_ASSIGNMENTS_WRITE,
    Capability.ABSENCES_MANAGE,
    Capability.PLANNING_SIGN,
    Capability.SWAP_APPROVE,
    Capability.POSITIONS_WRITE,
    Capability.HYGIENE_READ,
    Capability.HYGIENE_MANAGE,
    Capability.LEGAL_READ,
    Capability.FEEDBACK_READ,
    Capability.FEEDBACK_WRITE,
    Capability.CASH_READ,
    Capability.CASH_WRITE,
    Capability.NEWSLETTER_MANAGE,
    Capability.CANDIDATURES_MANAGE,
  ],
  chef: [
    Capability.MENU_EDIT_CATALOG,
    Capability.MENU_VALIDATE,
    Capability.MENU_COMPOSE,
    Capability.PLANNING_READ_OWN_LOCATIONS,
    Capability.ABSENCES_REQUEST,
    Capability.PLANNING_SIGN,
    Capability.PLANNING_EXTRA_REQUEST,
    Capability.HYGIENE_READ,
    Capability.FEEDBACK_READ,
    Capability.FEEDBACK_WRITE,
    Capability.CASH_READ,
    Capability.CASH_WRITE,
  ],
  staff: [
    Capability.PLANNING_READ_OWN_LOCATIONS,
    Capability.ABSENCES_REQUEST,
    Capability.PLANNING_SIGN,
    Capability.PLANNING_EXTRA_REQUEST,
    Capability.HYGIENE_READ,
    Capability.FEEDBACK_READ,
    Capability.FEEDBACK_WRITE,
    Capability.CASH_READ,
    Capability.CASH_WRITE,
  ],
};

/**
 * Calcule les capabilities effectives d'un user à partir de son rôle et
 * de ses extra_capabilities (mêmes règles que la fonction SQL
 * `public.has_capability` : défauts ∪ extra ∪ (admin.all ⇒ tout)).
 */
export function effectiveCapabilities(
  role: RoleKey,
  extra: readonly CapabilityKey[] = [],
): Set<CapabilityKey> {
  const set = new Set<CapabilityKey>(ROLE_DEFAULTS[role] ?? []);
  for (const c of extra) set.add(c);
  // admin.all débloque tout
  if (set.has(Capability.ADMIN_ALL)) {
    for (const c of Object.values(Capability)) set.add(c);
  }
  return set;
}

export function hasCapability(
  role: RoleKey,
  extra: readonly CapabilityKey[],
  cap: CapabilityKey,
): boolean {
  if (extra.includes(Capability.ADMIN_ALL)) return true;
  if (ROLE_DEFAULTS[role]?.includes(Capability.ADMIN_ALL)) return true;
  if (extra.includes(cap)) return true;
  return ROLE_DEFAULTS[role]?.includes(cap) ?? false;
}
